import { useEffect } from 'react';
import { cn } from '@/lib/cn';

type ToastTone = 'success' | 'error' | 'info';

const TONES: Record<ToastTone, { wrap: string; icon: string }> = {
  success: { wrap: 'bg-emerald-600 text-white', icon: 'ri-checkbox-circle-line' },
  error: { wrap: 'bg-rose-600 text-white', icon: 'ri-error-warning-line' },
  info: { wrap: 'bg-foreground-900 text-background-50', icon: 'ri-information-line' },
};

interface ToastProps {
  message: string;
  tone?: ToastTone;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ message, tone = 'success', onClose, duration = 3200 }: ToastProps) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [message, tone, duration, onClose]);

  const styles = TONES[tone];

  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-start px-4 md:px-6"
    >
      <div
        className={cn(
          'pointer-events-auto inline-flex max-w-[92vw] items-center gap-2.5 rounded-lg px-4 py-3 text-sm font-medium shadow-pop animate-toast-in',
          styles.wrap,
        )}
      >
        <i className={cn(styles.icon, 'text-lg leading-none')} aria-hidden="true" />
        {message}
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="ms-1 flex h-6 w-6 items-center justify-center rounded-md text-current/80 hover:bg-white/15 cursor-pointer"
        >
          <i className="ri-close-line text-lg" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}